import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Draggable } from 'react-beautiful-dnd';
import { Dropdown, Menu, Modal, Tooltip } from 'antd';
import { Icon } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import * as boardActions from '../../actions';
import * as drawerBoardActions from '../Drawer/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Content,
  MainContent,
  ItemTitle,
  ItemDescription,
  ItemId,
  UpdatedDate,
  MenuItem,
  TasksProgress,
  ProgressStyled,
  TasksTooltip
} from './styles';

const DraggableMarketingCard = memo(({ props, item, showDrawer }) => {
  const { provided, snapshot } = props;
  const { innerRef, draggableProps, dragHandleProps } = provided;
  const { isDragging } = snapshot;
  const dispatch = useDispatch();
  const marketing = useSelector(state => state.marketing);
  const currentBoardType = marketing.currentBoardType;
  
  // Set locale to Portuguese for time ago strings
  moment.locale('pt-br');
  
  const isRevised = useMemo(() => {
    return Boolean(item.is_revised);
  }, [item.is_revised]);

  const progress = useMemo(() => {
    if (!item.tasks || item.tasks.length === 0) return 0;
    return (item.tasks.filter(task => task.done).length / item.tasks.length) * 100;
  }, [item.tasks]);

  const tasksCompleted = useMemo(() => {
    if (!item.tasks || item.tasks.length === 0) return 0;
    return item.tasks.filter(task => task.done).length;
  }, [item.tasks]);

  const totalTasks = item.tasks ? item.tasks.length : 0;
  
  // Try updated_at first, fall back to created_at
  const dateField = item.updated_at || item.created_at;
  
  const timeAgo = useMemo(() => {
    if (!dateField) return null;
    return moment(dateField).fromNow();
  }, [dateField]);
  
  const fullDate = useMemo(() => {
    if (!dateField) return null;
    return moment(dateField).format('DD/MM/YYYY HH:mm');
  }, [dateField]);
  
  const handleClick = useMemo(() => () => showDrawer(item), [showDrawer, item]);

  const onClick = ({ key }) => {
    if (key === '1') {
      dispatch(boardActions.duplicateItem(item, currentBoardType));
    } else if (key === '2') {
      Modal.confirm({
        title: `Você tem certeza que deseja deletar o item "${item.title}"?`,
        okText: 'Sim',
        cancelText: 'Não',
        onOk: () => {
          dispatch(drawerBoardActions.remove(item, currentBoardType));
        }
      });
    }
  };

  const menu = (
    <Menu onClick={onClick}>
      <Menu.Item key="1">
        <Icon type="copy" /> Duplicar
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="2">
        <Icon type="delete" /> Deletar
      </Menu.Item>
    </Menu>
  );

  return (
    <Container
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      isDragging={isDragging}
      isRevised={isRevised}
      onClick={handleClick}
      className="marketing-card-container"
    >
      <ItemId>#{item.id}</ItemId>
      {dateField && (
        <Tooltip title={`Atualizado em: ${fullDate}`} placement="top">
          <UpdatedDate>{timeAgo}</UpdatedDate>
        </Tooltip>
      )}
      <MenuItem onClick={(e) => e.stopPropagation()}>
        <Dropdown overlay={menu} placement="bottomRight">
          <Icon type="more" />
        </Dropdown>
      </MenuItem>
      
      <Content>
        <MainContent>
          <ItemTitle>{item.title}</ItemTitle>
          {totalTasks > 0 && (
            <TasksProgress>
              <Tooltip 
                title={`${tasksCompleted}/${totalTasks} tarefas concluídas`}
                placement="top"
              >
                <ProgressStyled percent={progress} />
              </Tooltip>
            </TasksProgress>
          )}
        </MainContent>
      </Content>
    </Container>
  );
});

export const MarketingCard = memo(({ index, item, showDrawer }) => (
  <Draggable key={index} draggableId={String(item.uid)} index={index}>
    {(provided, snapshot) => (
      <DraggableMarketingCard
        props={{ provided, snapshot }}
        item={item}
        showDrawer={showDrawer}
      />
    )}
  </Draggable>
));

MarketingCard.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    uid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    tasks: PropTypes.array,
    is_revised: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    updated_at: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  showDrawer: PropTypes.func.isRequired,
};

export default MarketingCard;